import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent, ThemeEvents } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenuHeading extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(() => this.#preloadImages);

    document.addEventListener(ThemeEvents.menuHoverEnd, () => this.#debouncedDeactivate());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Get the default target
   * @returns {HTMLElement | null}
   */
  get #defaultTarget() {
    return /** @type {HTMLElement | null} */ this.querySelector('.menu-list__list-item');
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    this.#activateItem(event.target);
  }

  /**
   * Activate the selected menu item
   * @param {Element | null} target
   */
  #activateItem = (target) => {
    if (!(target instanceof Element)) return null;

    let item = findMenuItem(target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.querySelector('.menu-list__link--active');

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      previouslyActiveItem.classList.remove('menu-list__link--active');
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    item.classList.add('menu-list__link--active');

    const headerMenu = findHeaderMenu(this);
    let headerMenuItem = findHeaderMenuItem(item, headerMenu);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!headerMenuItem && !isDefaultSlot) {
      headerMenuItem = this.overflowMenu;
    }

    const submenuHeight = headerMenuItem ? Math.max(headerMenuItem.offsetHeight, overflowMenuHeight) : 0;

    if (headerMenu && headerMenuItem) {
      headerMenu.querySelectorAll('.header-menu__inner').forEach(elem => elem.classList.remove('header-menu__inner--active'));
      headerMenuItem.classList.add('header-menu__inner--active');
    }
    // this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    // this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately and return to the default
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.#activateItem(this.#defaultTarget);

    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu-heading')) {
  customElements.define('header-menu-heading', HeaderMenuHeading);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the corresponding header menu to this heading.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findHeaderMenu(element) {
  const header = element?.closest('header-component');
  const menu = header?.querySelector('[ref="headerMenu"]');
  return menu instanceof HTMLElement ? menu : null;
}

/**
 * Find the corresponding header menu item to this heading.
 * @param {Element | null | undefined} element
 * @param {HTMLElement | null | undefined} headerMenu
 * @returns {HTMLElement | null}
 */
function findHeaderMenuItem(element, headerMenu) {
  const menuItemId = element?.closest('.menu-list__list-item')?.id;
  const menuNum = menuItemId?.substring(menuItemId.length - 1)
  const menu = headerMenu?.querySelector("#header-menu-" + menuNum);
  return menu instanceof HTMLElement ? menu : null;
}
